// Peças comuns do painel Aplicativo (Marketing → Aplicativo, 14/09/2026).
// Backend: server/routes/app-twms.cjs → proxy para o app.twms (lib/painel-app.js).
import { useCallback, useEffect, useRef, useState, type ReactNode, type ComponentType } from 'react'
import { FiLoader, FiAlertCircle, FiRefreshCw } from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import { dataHoraCurta } from '../../lib/mascaras'
import { FotoPessoa } from '../foto/FotoPessoa'

export const API_APP = '/api/app-twms'

export const PAPEL: Record<string, string> = {
  distribuicao: 'Distribuição', empresario: 'Empresário', lider: 'Líder', consultora: 'Consultora',
}

export const num = (n: number | null | undefined) => Number(n || 0).toLocaleString('pt-BR')
export const quando = dataHoraCurta

/** "há 3 h", "há 2 dias", "ontem" — o dia exato fica no `title`. */
export function ha(iso: string | null | undefined): string {
  if (!iso) return 'nunca'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return '—'
  const min = Math.round(ms / 60000)
  if (min < 2) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.round(h / 24)
  if (d === 1) return 'ontem'
  if (d < 45) return `há ${d} dias`
  return `há ${Math.round(d / 30)} meses`
}

/** GET com o token do CRM; `recarregar` refaz. Mantém o dado velho enquanto busca. */
export function useApp<T>(url: string | null) {
  const { authenticatedFetch } = useAuth()
  const [dados, setDados] = useState<T | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  const atual = useRef(url)
  atual.current = url

  const recarregar = useCallback(async () => {
    if (!url) return
    setCarregando(true); setErro(null)
    try {
      const r = await authenticatedFetch(url)
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      if (atual.current === url) setDados(j)
    } catch (e) {
      if (atual.current === url) setErro(e instanceof Error ? e.message : 'falhou')
    } finally {
      if (atual.current === url) setCarregando(false)
    }
  }, [url, authenticatedFetch])

  useEffect(() => { recarregar() }, [recarregar])
  return { dados, erro, carregando, recarregar }
}

/** POST/PUT com corpo JSON; devolve { ok, dados, erro }. */
export function useEnviar() {
  const { authenticatedFetch } = useAuth()
  return useCallback(async (url: string, corpo: unknown = {}, method = 'POST') => {
    try {
      const r = await authenticatedFetch(url, { method, body: JSON.stringify(corpo) })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) return { ok: false, dados: j, erro: j?.error || `HTTP ${r.status}` }
      return { ok: true, dados: j, erro: null as string | null }
    } catch (e) {
      return { ok: false, dados: null, erro: e instanceof Error ? e.message : 'falhou' }
    }
  }, [authenticatedFetch])
}

export function Carregando() {
  return (
    <div className="h-full flex items-center justify-center text-muted-foreground">
      <FiLoader className="w-5 h-5 animate-spin" />
    </div>
  )
}

export function Falha({ erro, tentar }: { erro: string; tentar: () => void }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground p-6 text-center">
      <FiAlertCircle className="w-7 h-7 opacity-40" />
      <p className="text-sm">{erro}</p>
      <button onClick={tentar} className="text-xs underline">tentar de novo</button>
    </div>
  )
}

/** Título + chips de número NA MESMA LINHA (regra dos painéis minimal). */
export function Cabeca({ titulo, sub, chips, direita, carregando, recarregar }: {
  titulo: ReactNode; sub?: ReactNode; chips?: ReactNode; direita?: ReactNode
  carregando?: boolean; recarregar?: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 shrink-0 flex-wrap">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-foreground">{titulo}</h2>
        {sub && <p className="text-[11.5px] text-muted-foreground mt-0.5">{sub}</p>}
        {chips && <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">{chips}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {direita}
        {recarregar && (
          <button onClick={recarregar} disabled={carregando} title="Atualizar" aria-label="Atualizar"
            className="neu-relief inline-flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-foreground">
            <FiRefreshCw className={`w-3.5 h-3.5 ${carregando ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
    </div>
  )
}

export function Chip({ children, tom = 'neutro', title, ativo, onClick }: {
  children: ReactNode; tom?: 'neutro' | 'bom' | 'alerta' | 'ruim' | 'info'; title?: string; ativo?: boolean; onClick?: () => void
}) {
  const cor = {
    neutro: 'text-muted-foreground',
    bom: 'text-emerald-700 dark:text-emerald-300',
    alerta: 'text-amber-700 dark:text-amber-300',
    ruim: 'text-red-700 dark:text-red-300',
    info: 'text-sky-700 dark:text-sky-300',
  }[tom]
  const cls = `neu-chip px-2.5 py-0.5 rounded-full text-[11px] tabular-nums inline-flex items-center gap-1 whitespace-nowrap ${cor} ${ativo ? 'font-semibold text-foreground ring-1 ring-foreground/25' : ''}`
  return onClick
    ? <button type="button" title={title} onClick={onClick} aria-pressed={ativo} className={`${cls} cursor-pointer hover:text-foreground`}>{children}</button>
    : <span title={title} className={cls}>{children}</span>
}

/** Selo curto dentro de uma linha (usando, bloqueada…). */
export function Selo({ children, tom = 'neutro', title }: { children: ReactNode; tom?: 'neutro' | 'bom' | 'alerta' | 'ruim' | 'info'; title?: string }) {
  const cor = {
    neutro: 'bg-foreground/[0.06] text-muted-foreground',
    bom: 'bg-emerald-500/12 text-emerald-800 dark:text-emerald-300',
    alerta: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
    ruim: 'bg-red-500/12 text-red-800 dark:text-red-300',
    info: 'bg-sky-500/12 text-sky-800 dark:text-sky-300',
  }[tom]
  return <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-px rounded-md text-[10.5px] font-semibold whitespace-nowrap ${cor}`}>{children}</span>
}

/** Número grande com rótulo — só onde o número É o assunto (Visão geral). */
export function Numero({ icon: Icon, rotulo, valor, nota, tom }: {
  icon?: ComponentType<{ className?: string }>; rotulo: string; valor: ReactNode; nota?: ReactNode; tom?: 'alerta' | 'ruim' | 'bom'
}) {
  const corValor = tom === 'alerta' ? 'text-amber-700 dark:text-amber-300' : tom === 'ruim' ? 'text-red-700 dark:text-red-300' : tom === 'bom' ? 'text-emerald-700 dark:text-emerald-300' : ''
  return (
    <div className="neu-card rounded-2xl px-3.5 py-3 flex flex-col gap-1 min-w-0">
      <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}{rotulo}
      </span>
      <span className={`text-[22px] font-semibold tabular-nums leading-none ${corValor}`}>{valor}</span>
      {nota && <span className="text-[11px] text-muted-foreground truncate">{nota}</span>}
    </div>
  )
}

/** Seletor segmentado pequeno (7 × 30 dias, Arquivos × No app…). */
export function Segmento<T extends string>({ valor, opcoes, aoMudar, rotulo }: {
  valor: T; opcoes: [T, string][]; aoMudar: (v: T) => void; rotulo: string
}) {
  return (
    <div className="neu-seg rounded-full p-0.5 inline-flex gap-0.5" role="group" aria-label={rotulo}>
      {opcoes.map(([k, t]) => (
        <button key={k} type="button" onClick={() => aoMudar(k)} aria-pressed={valor === k}
          className={`px-3 py-1 rounded-full text-[12px] font-medium whitespace-nowrap ${valor === k ? 'neu-relief bg-primary text-primary-foreground' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
          {t}
        </button>
      ))}
    </div>
  )
}

/** A foto do painel Aplicativo — desde 15/09/2026 é a `FotoPessoa` comum a todo o CRM (clica e amplia). */
export function Avatar({ foto, nome, tamanho = 36, expandir = true }: { foto?: string | null; nome?: string | null; tamanho?: number; expandir?: boolean }) {
  return <FotoPessoa foto={foto} nome={nome} tamanho={tamanho} expandir={expandir} />
}

export const titulo = (s: string | null | undefined) =>
  String(s || '').toLowerCase().replace(/(^|\s|\/)(\p{L})/gu, (_m, a, b) => a + b.toUpperCase())
